import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'

import { DateRangePicker, type DateRange } from '@/components/common/DatePicker/DateRangePicker'
import { Icon } from '@/components/icons'
import { toast } from '@/components/common/Toast'
import { Button, Input, Label } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useInitialLocationFilter } from '@/hooks/useInitialLocationFilter'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import {
  useTempWorker,
  useTempWorkerWorkSummary,
  useUpdateTempWorker,
} from '@/modules/temp-worker/services/tempWorker'
import { TempWorkerSettlementPanel } from '@/components/pages/Admin/TempWorkers/TempWorkerSettlementPanel'
import { TempWorkSessionRowActions } from '@/components/pages/Admin/TempWorkers/TempWorkSessionRowActions'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/utils/number'

function ymdToDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

function dateToYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function startOfCurrentMonthYmd(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function todayLocalYmd(): string {
  return dateToYmd(new Date())
}

function avatarInitial(name: string) {
  const s = name.trim()
  if (!s) return '?'
  const ch = Array.from(s)[0]
  return ch?.toUpperCase() ?? '?'
}

function branchLabelKey(loc: BillLocationValue): `location_${BillLocationValue}` {
  return `location_${loc}`
}

function CardShell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card shadow-sm shadow-black/5',
        className,
      )}
    >
      {children}
    </div>
  )
}

type TempWorkerDetailProps = {
  workerId: string
}

export function TempWorkerDetail({ workerId }: TempWorkerDetailProps) {
  const t = useTranslations('temp_worker_detail')
  const tTempWorkers = useTranslations('temp_workers')
  const tButtons = useTranslations('buttons')
  const locale = useLocale()

  const [startDate, setStartDate] = useState(() => startOfCurrentMonthYmd())
  const [endDate, setEndDate] = useState(() => todayLocalYmd())
  const [locationFilter, setLocationFilter] = useInitialLocationFilter()
  const [page, setPage] = useState(1)
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editPosition, setEditPosition] = useState('')

  const { data: workerRes, isLoading: workerLoading } = useTempWorker(workerId)
  const summaryParams = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      page,
      limit: 20,
      ...(locationFilter ? { location: locationFilter } : {}),
    }),
    [startDate, endDate, locationFilter, page],
  )
  const { data: summaryRes, isLoading: summaryLoading, refetch } = useTempWorkerWorkSummary(
    workerId,
    summaryParams,
  )
  const $update = useUpdateTempWorker()

  useEffect(() => {
    setPage(1)
  }, [startDate, endDate, locationFilter])

  const worker = workerRes?.data
  const summary = summaryRes?.data?.summary
  const sessions = summaryRes?.data?.sessions ?? []
  const meta = summaryRes?.meta

  const dateRangeValue = useMemo((): DateRange | undefined => {
    if (!startDate && !endDate) return undefined
    return {
      from: startDate ? ymdToDate(startDate) : undefined,
      to: endDate ? ymdToDate(endDate) : undefined,
    }
  }, [startDate, endDate])

  const onDateRangeChange = (r: DateRange | undefined) => {
    if (!r?.from && !r?.to) {
      setStartDate(startOfCurrentMonthYmd())
      setEndDate(todayLocalYmd())
      return
    }
    setStartDate(r.from ? dateToYmd(r.from) : startDate)
    setEndDate(r.to ? dateToYmd(r.to) : endDate)
  }

  const formatWorkDate = (iso: string) => {
    const d = ymdToDate(iso)
    return d.toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(locale === 'th' ? 'th-TH' : 'en-US', {
      dateStyle: 'short',
      timeStyle: 'medium',
    })

  const openEdit = () => {
    if (!worker) return
    setEditName(worker.name)
    setEditPhone(worker.phone ?? '')
    setEditPosition(worker.position ?? '')
    setEditOpen(true)
  }

  const onSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = editName.trim()
    if (!name) return
    try {
      await $update.mutateAsync({
        id: workerId,
        name,
        phone: editPhone.trim() || undefined,
        position: editPosition.trim() || undefined,
      })
      toast.toastSuccess(t('updated'))
      setEditOpen(false)
      refetch()
    } catch (err: any) {
      toast.toastFailed(err?.message || t('update_fail'))
    }
  }

  if (workerLoading) {
    return <div className="p-8 text-sm text-muted-foreground">{t('loading')}</div>
  }

  if (!worker) {
    return <div className="p-8 text-sm text-muted-foreground">{t('not_found')}</div>
  }

  const shortId = worker.id.slice(-8).toUpperCase()
  const currentPage = meta?.currentPage ?? page
  const totalPages = meta?.totalPages ?? 1

  return (
    <div className="w-full min-w-0 max-w-none space-y-8 pb-10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 gap-4">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xl font-bold text-primary">
            {avatarInitial(worker.name)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{worker.name}</h1>
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  worker.active
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {worker.active ? t('active') : t('inactive')}
              </span>
            </div>
            {worker.position ? (
              <p className="mt-1 text-sm text-muted-foreground">{worker.position}</p>
            ) : null}
            {worker.phone ? (
              <p className="text-sm text-muted-foreground">
                {t('phone')}: {worker.phone}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t('worker_id')}: {shortId}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/temp-workers">
              <Icon icon="mdi:arrow-left" className="mr-2 size-4" />
              {t('back')}
            </Link>
          </Button>
          <Button
            type="button"
            className="bg-slate-800 text-white hover:bg-slate-900"
            onClick={openEdit}
          >
            <Icon icon="mdi:pencil" className="mr-2 size-4" />
            {t('edit')}
          </Button>
        </div>
      </div>

      <TempWorkerSettlementPanel worker={worker} />

      <CardShell className="p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="min-w-[220px] flex-1">
            <Label className="text-xs text-muted-foreground">{t('date_range')}</Label>
            <DateRangePicker
              className="mt-1.5 h-10 w-full justify-between"
              value={dateRangeValue}
              onChange={onDateRangeChange}
            />
          </div>
          <div className="min-w-[180px] flex-1">
            <Label className="text-xs text-muted-foreground">{t('location_filter')}</Label>
            <Select
              value={locationFilter || '__all__'}
              onValueChange={(v) => setLocationFilter(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="mt-1.5 h-10">
                <SelectValue placeholder={t('all_locations')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('all_locations')}</SelectItem>
                {BILL_LOCATION_VALUES.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {t(branchLabelKey(loc))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardShell>

      {summary && summary.open_sessions > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {t('open_sessions_warning', { count: summary.open_sessions })}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title={t('card_total_pay')}
          value={`${formatPrice(summary?.total_pay_baht ?? 0)} ${t('baht')}`}
          hint={t('card_total_pay_hint')}
          icon="mdi:cash-multiple"
          accent="blue"
        />
        <SummaryCard
          title={t('card_working_days')}
          value={String(summary?.working_days ?? 0)}
          icon="mdi:calendar-check"
        />
        <SummaryCard
          title={t('card_closed_sessions')}
          value={String(summary?.closed_sessions ?? 0)}
          icon="mdi:clipboard-check-outline"
        />
        <SummaryCard
          title={t('card_total_hours')}
          value={`${(summary?.total_hours ?? 0).toFixed(1)} ${t('hours_unit')}`}
          icon="mdi:clock-outline"
        />
      </div>

      <CardShell>
        <div className="border-b border-border/60 px-6 py-4">
          <h2 className="text-lg font-semibold tracking-tight">{t('history_title')}</h2>
        </div>
        {summaryLoading ? (
          <div className="p-8 text-sm text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-muted-foreground">
                  <th className="px-6 py-3 font-medium">{t('col_date')}</th>
                  <th className="px-6 py-3 font-medium">{t('col_location')}</th>
                  <th className="px-6 py-3 font-medium">{t('col_time')}</th>
                  <th className="max-w-[200px] px-6 py-3 font-medium">{tTempWorkers('col_note')}</th>
                  <th className="px-6 py-3 font-medium text-right">{t('col_pay')}</th>
                  <th className="px-6 py-3 font-medium">{t('col_status')}</th>
                  <th className="px-6 py-3 font-medium">{tTempWorkers('col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((row) => {
                  const isOpen = row.check_out_at == null
                  const isPaid =
                    !isOpen &&
                    Boolean(
                      worker.paid_through_work_date &&
                        row.work_date <= worker.paid_through_work_date,
                    )
                  const loc =
                    row.location && BILL_LOCATION_VALUES.includes(row.location as BillLocationValue)
                      ? t(branchLabelKey(row.location as BillLocationValue))
                      : t('unknown_branch')
                  return (
                    <tr key={row.id} className="border-b border-border/40 last:border-0">
                      <td className="whitespace-nowrap px-6 py-3">{formatWorkDate(row.work_date)}</td>
                      <td className="px-6 py-3">{loc}</td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {formatDateTime(row.check_in_at)}
                        {' – '}
                        {row.check_out_at ? formatDateTime(row.check_out_at) : t('no_pay')}
                      </td>
                      <td className="max-w-[200px] px-6 py-3 text-muted-foreground">
                        <span className="line-clamp-2 whitespace-pre-wrap break-words">
                          {row.note?.trim() ? row.note : tTempWorkers('no_note')}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-right font-medium tabular-nums">
                        {row.pay_baht != null ? formatPrice(row.pay_baht) : t('no_pay')}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                            isOpen
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
                              : isPaid
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                                : 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
                          )}
                        >
                          {isOpen
                            ? t('status_open')
                            : isPaid
                              ? t('status_paid')
                              : t('status_unpaid')}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <TempWorkSessionRowActions
                          session={row}
                          paidThroughWorkDate={worker.paid_through_work_date ?? null}
                          namespace="temp_worker_detail"
                          compact
                          onSuccess={(_kind) => {
                            void refetch()
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {sessions.length === 0 ? (
              <p className="border-t border-border/40 px-6 py-6 text-center text-sm text-muted-foreground">
                {t('empty_history')}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-6 py-4 text-sm">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {tButtons('previous')}
          </Button>
          <span className="text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {tButtons('next')}
          </Button>
        </div>
      </CardShell>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('edit_title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSaveEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">{t('name')}</Label>
              <Input
                id="edit-name"
                className="mt-1.5"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-position">{t('position')}</Label>
              <Input
                id="edit-position"
                className="mt-1.5"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">{t('phone')}</Label>
              <Input
                id="edit-phone"
                className="mt-1.5"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={$update.isLoading}>
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SummaryCard({
  title,
  value,
  hint,
  icon,
  accent,
}: {
  title: string
  value: string
  hint?: string
  icon: string
  accent?: 'blue'
}) {
  return (
    <CardShell
      className={cn(
        'p-5',
        accent === 'blue' && 'border-l-4 border-l-blue-600',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">{title}</p>
        <Icon icon={icon} className="size-5 shrink-0 text-muted-foreground" />
      </div>
      <p className="mt-2 text-2xl font-bold tracking-tight">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </CardShell>
  )
}
